import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';

// Default images for convocatorias without custom images
const DEFAULT_IMAGES = [
  'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop',
  'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=250&fit=crop',
  'https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=400&h=250&fit=crop',
  'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=250&fit=crop',
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=250&fit=crop'
];

export default function ConvocatoriasDisponibles() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [convocatorias, setConvocatorias] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConvocatorias();
  }, []);

  const fetchConvocatorias = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/convocatorias/publicas/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar convocatorias');
      }

      const data = await response.json();
      setConvocatorias(data);
    } catch (error) {
      toast.error('Error al cargar las convocatorias');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getImageForConvocatoria = (conv, index) => {
    if (conv.imagen_url) {
      let imageUrl = conv.imagen_url;
      // Handle legacy URLs that don't have /api prefix
      if (imageUrl.startsWith('/uploads/')) {
        imageUrl = '/api' + imageUrl;
      }
      // If it's already a full URL, return as-is; otherwise prepend API_URL
      return imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`;
    }
    return DEFAULT_IMAGES[index % DEFAULT_IMAGES.length];
  };

  const getEstadoBadge = (estado) => {
    if (estado === 'Activa') {
      return (
        <span className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          ¡Inscríbete ahora!
        </span>
      );
    }
    if (estado === 'Próxima') {
      return (
        <span className="absolute top-3 right-3 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          ¡Nueva convocatoria!
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Convocatorias Disponibles
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver al Panel</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Explora nuestras convocatorias
          </h2>
          <p className="text-slate-600">
            Encuentra la convocatoria ideal para ti y postúlate
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-lg text-slate-500">Cargando convocatorias...</div>
          </div>
        ) : convocatorias.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border border-slate-200">
            <p className="text-slate-500 text-lg">No hay convocatorias disponibles en este momento</p>
            <p className="text-slate-400 mt-2">Vuelve pronto para ver nuevas oportunidades</p>
          </div>
        ) : (
          /* Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {convocatorias.map((conv, index) => (
              <div 
                key={conv.id}
                className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={getImageForConvocatoria(conv, index)}
                    alt={conv.nombre}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = DEFAULT_IMAGES[index % DEFAULT_IMAGES.length];
                    }}
                  />
                  {getEstadoBadge(conv.estado)}
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Title */}
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2 line-clamp-2">
                    {conv.nombre}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-600 mb-4 line-clamp-4">
                    {conv.descripcion}
                  </p>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-3 mb-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>Hasta: {conv.fecha_final}</span>
                    </div>
                    {conv.area && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{conv.area}</span>
                      </div>
                    )}
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => navigate(`/postular/${conv.id}`)}
                    className="w-full bg-[#0F172A] text-white font-semibold py-3 px-4 rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    Postularse
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
